import { useState, useCallback } from "react"
import { useToast } from "./use-toast"
import { useParams, useRouter } from "next/navigation"
import { apiFetch } from "@/lib/api/apiFetch"
import { useListContext } from "@/components/providers/list-provider"

/**
 * Contact information within a recipient list
 */
interface Contact {
    _id: string
    email: string
    name: string
    status: string
    source: string
    location?: string
}

/**
 * Recipient list summary model
 */
interface RecipientList {
    _id: string
    name: string
    description: string
    contactCount: number
    createdAt: string
    updatedAt: string
    remarks: string
    contacts: Contact[]
    isActive: boolean
}

/**
 * API response for recipient list listing
 */
interface ApiResponse {
    lists: RecipientList[]
    pagination: {
        page: number
        limit: number
        total: number
        pages: number
    }
    error?: string
}

/**
 * Contact details for list details view
 */
interface ContactDetails {
    _id: string
    email: string
    name: string
    status: any
    source: string
    location?: string
    createdAt: string
}

/**
 * Recipient list detailed model
 */
interface RecipientListDetails {
    _id: string
    name: string
    description: string
    contactCount: number
    createdAt: string
    updatedAt: string
    remarks: string
    contacts: ContactDetails[]
    isActive: boolean
}

/**
 * useListHook
 * @author Arpita Dutta
 * ===========
 * Custom React hook for managing recipient lists and list details.
 *
 * This hook encapsulates:
 * - Fetching recipient lists with pagination and search
 * - Fetching recipient list details by ID
 * - Archiving and deleting recipient lists
 * - Navigation to list detail pages
 * - Centralized loading, error, and toast handling
 *
 * @returns {{
 *  fetchRecipientLists: (page?: number, search?: string) => Promise<void>,
 *  handleViewDetails: (list: RecipientList) => void,
 *  handleDeleteList: (listId: string) => Promise<void>,
 *  confirmArchiveList: () => Promise<void>,
 *  handleArchiveList: (listId: string) => void,
 *  error: string | null,
 *  isLoading: boolean,
 *  lists: RecipientList[],
 *  pagination: {
 *      page: number,
 *      limit: number,
 *      total: number,
 *      pages: number
 *  },
 *  listToArchive: RecipientList | null,
 *  archivingLists: Set<string>,
 *  getStatusColor: (status: string) => string,
 *  showArchiveDialog: boolean,
 *  setShowArchiveDialog: (value: boolean) => void,
 *  fetchListDetails: () => Promise<void>,
 *  listDetails: RecipientListDetails | null,
 *  handleDeleteListDetails: () => Promise<void>
 * }}
 */
export const useListHook = () => {
    /** Array of recipient lists shown in list view */
    const [lists, setLists] = useState<RecipientList[]>([])

    /** Detailed data of a single recipient list */
    const [listDetails, setListDetails] = useState<RecipientListDetails | null>(null)

    /** Global loading state for list and detail API calls */
    const [isLoading, setIsLoading] = useState(true)

    /** Error message from API failures */
    const [error, setError] = useState<string | null>(null)

    /**
     * Pagination state
     * - page: current page number
     * - limit: items per page
     * - total: total number of items
     * - pages: total number of pages
     */
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
    })

    /** Controls visibility of archive confirmation dialog */
    const [showArchiveDialog, setShowArchiveDialog] = useState(false)

    /** Stores the recipient list selected for archive */
    const [listToArchive, setListToArchive] = useState<RecipientList | null>(null)

    /**
     * Tracks IDs of lists currently being archived
     * Used to disable buttons / show loaders per list
     */
    const [archivingLists, setArchivingLists] = useState<Set<string>>(new Set())

    /** Loading state for list export */
    const [exportingList, setExportingList] = useState(false)


    const { toast } = useToast()
    const router = useRouter()
    const params = useParams()

    /**
     * Fetch recipient lists from the server
     *
     * Side effects:
     * - Updates `lists` state
     * - Updates `pagination` state
     * - Sets loading and error states
     * - Shows error toast on failure
     *
     * @param {number} page - Page number to fetch
     * @param {string} search - Search keyword for filtering lists
     * @returns {Promise<void>}
     */
    const fetchRecipientLists = async (page = 1, search = "", limit = pagination.limit) => {
        try {
            setIsLoading(true)
            setError(null)

            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search }),
            })

            const data: ApiResponse = await apiFetch(`/api/recipient-lists?${params}`)
            setLists(data.lists)
            setPagination(data.pagination)
        } catch {
            setError("Failed to fetch recipient lists")
            toast({
                title: "Error",
                description: "Failed to fetch recipient lists",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Opens the archive confirmation dialog for a selected list
     *
     * Side effects:
     * - Sets `listToArchive`
     * - Opens archive confirmation modal
     *
     * @param {string} listId - ID of the list to archive
     * @returns {void}
     */
    const handleArchiveList = async (listId: string) => {
        const list = lists.find(l => l._id === listId)
        if (!list) return

        setListToArchive(list)
        setShowArchiveDialog(true)
    }

    /**
     * Confirms and archives the selected recipient list
     *
     * Side effects:
     * - Sends archive API request
     * - Removes archived list from state
     * - Updates `archivingLists`
     * - Shows success or error toast
     * - Resets archive dialog state
     *
     * @returns {Promise<void>}
     */
    const confirmArchiveList = async () => {
        if (!listToArchive) return

        try {
            setArchivingLists(prev => new Set(prev).add(listToArchive._id))

            await apiFetch(`/api/recipient-lists/${listToArchive._id}/manage`, {
                method: "PUT",
                data: { action: "archive" },
            })

            toast({
                title: "Success",
                description: "List archived successfully",
            })

            setLists(prev => prev.filter(l => l._id !== listToArchive._id))
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to archive list",
                variant: "destructive",
            })
        } finally {
            setArchivingLists(prev => {
                const newSet = new Set(prev)
                newSet.delete(listToArchive._id)
                return newSet
            })
            setShowArchiveDialog(false)
            setListToArchive(null)
        }
    }

    /**
     * Deletes a recipient list from the list view
     *
     * Side effects:
     * - Shows browser confirmation dialog
     * - Sends delete API request
     * - Removes deleted list from state
     * - Shows success or error toast
     *
     * @param {string} listId - Recipient list ID
     * @returns {Promise<void>}
     */
    const handleDeleteList = async (listId: string) => {
        if (!confirm("Are you sure you want to delete this recipient list?")) {
            return
        }

        try {
            await apiFetch(`/api/recipient-lists/${listId}`, {
                method: "DELETE",
            })

            setLists(lists.filter(list => list._id !== listId))
            toast({
                title: "Success",
                description: "Recipient list deleted successfully",
            })
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to delete recipient list",
                variant: "destructive",
            })
        }
    }

    /**
     * Navigates to recipient list details page
     *
     * @param {RecipientList} list - Selected recipient list
     * @returns {void}
     */
    const handleViewDetails = async (list: RecipientList) => {
        router.push(`/lists/${list._id}`)
    }

    /**
     * Fetch detailed recipient list data by ID
     *
     * Side effects:
     * - Updates `listDetails`
     * - Sets loading and error state
     * - Shows error toast on failure
     *
     * @returns {Promise<void>}
     */
    const fetchListDetails = async () => {
        try {
            setIsLoading(true)
            setError(null)

            const data = await apiFetch(`/api/recipient-lists/${params?.id}`)
            setListDetails(data)
        } catch (error: any) {
            setError(error.message || "Failed to fetch list details")
            toast({
                title: "Error",
                description: error.message || "Failed to fetch list details",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Deletes recipient list from the details page
     *
     * Side effects:
     * - Shows browser confirmation dialog
     * - Sends delete API request
     * - Redirects user back to list page on success
     *
     * @returns {Promise<void>}
     */
    const handleDeleteListDetails = async () => {
        if (!confirm("Are you sure you want to delete this recipient list?")) {
            return
        }

        try {
            await apiFetch(`/api/recipient-lists/${params?.id}`, {
                method: "DELETE",
            })

            toast({
                title: "Success",
                description: "Recipient list deleted successfully",
            })
            router.push("/lists")
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to delete recipient list",
                variant: "destructive",
            })
        }
    }

    /**
     * Returns Tailwind CSS classes based on contact status
     *
     * Used for status badge styling in UI
     *
     * @param {string} status - Contact status value
     * @returns {string} Tailwind CSS class string
     */
    const getStatusColor = (status: string) => {
        switch (status) {
            case "subscribed":
                return "bg-green-100 text-green-800"
            case "unsubscribed":
                return "bg-red-100 text-red-800"
            case "pending-confirmation":
                return "bg-yellow-100 text-yellow-800"
            case "warm-lead":
                return "bg-blue-100 text-blue-800"
            case "trial-user":
                return "bg-purple-100 text-purple-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    /**
     * Exports contacts of a recipient list as a downloadable CSV file
     *
     * Side effects:
     * - Fetches CSV blob from export API
     * - Triggers browser file download
     * - Shows success or error toast
     * - Tracks loading state via `exportingList`
     *
     * @param {string} listId - Recipient list ID to export
     * @returns {Promise<void>}
     */
    const handleExportList = useCallback(async (listId: string) => {
        try {
            setExportingList(true)

            const params = new URLSearchParams({ listId })

            const blob = await apiFetch(
                `/api/recipient-lists/export?${params.toString()}`,
                { method: "GET", responseType: "blob" }
            )

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `recipient-list-export.csv`

            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)

            toast({
                title: "Success",
                description: "List exported successfully",
            })
        } catch (e: any) {
            toast({
                title: "Export failed",
                description: e.message || "Failed to export list",
                variant: "destructive",
            })
        } finally {
            setExportingList(false)
        }
    }, [toast])

    /**
     * Tracks IDs of lists currently being revalidated (Global State)
     */
    const { revalidatingLists, setRevalidatingLists, addRevalidatingList, clearRevalidatingList } = useListContext()
    const [revalidating, setRevalidating] = useState(false)

    const revalidateList = async (listId: string) => {
        setRevalidating(true)
        addRevalidatingList(listId)
        try {
            const data = await apiFetch(`/api/recipient-lists/${listId}/revalidate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            if (!data.error && data.code === "REVALIDATED_PROCESSING") {
                toast({
                    title: "Success",
                    description: "Revalidation process started. Once completed, you will be notified.",
                    variant: "default",
                })
                // We don't clear the listId here because it's a background process.
                // The socket listener in DashboardHeader will clear it.
            } else {
                clearRevalidatingList(listId)
                fetchRecipientLists()
                toast({
                    title: "Error",
                    description: data.message || "Failed to revalidate list",
                    variant: "destructive",
                })
            }
        } catch (err: any) {
            clearRevalidatingList(listId)
            fetchRecipientLists()
            toast({
                title: "Error",
                description: err.message || "Failed to revalidate list",
                variant: "destructive",
            })
        } finally {
            setRevalidating(false)
        }
    }


    return {
        fetchRecipientLists,
        handleViewDetails,
        handleDeleteList,
        confirmArchiveList,
        handleArchiveList,
        error,
        isLoading,
        lists,
        pagination,
        listToArchive,
        archivingLists,
        getStatusColor,
        showArchiveDialog,
        setShowArchiveDialog,
        fetchListDetails,
        listDetails,
        handleDeleteListDetails,
        handleExportList,
        exportingList,
        revalidateList,
        setRevalidatingLists,
        revalidatingLists,
        revalidating,
        clearRevalidatingList
    }
}
